import { Subscription } from 'rxjs'
import { getSearchState } from './search-results.selectors'
import {
  FetchSearchResultsRequest,
  UpdateField,
} from './search-results.actions'
import { Store, select } from '@ngrx/store'
import { Component, OnInit, OnDestroy } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import ListTrait from '@traits/list.trait'
import { formatDate, formatNumber } from '@angular/common'
import { SearchType } from '@shared/shared.types'
import { getSettingsState } from '@containers/settings/settings.selectors'

@Component({
  selector: 'app-search-results',
  templateUrl: './search-results.component.html',
  styleUrls: ['./search-results.component.css'],
})
export class SearchResultsComponent
  extends ListTrait
  implements OnInit, OnDestroy
{
  searchType: string
  state$: Subscription
  route$: Subscription
  isLoading: boolean
  public columns: object[] = []
  public currency: any
  private settings$: Subscription

  constructor(
    public store: Store<any>,
    public router: Router,
    private route: ActivatedRoute,
  ) {
    super(router)
  }

  ngOnInit() {
    this.settings$ = this.store
      .pipe(select(getSettingsState))
      .subscribe((state) => {
        this.currency = state.getIn([
          'data',
          'units',
          'SETTINGS_UNITS_CURRENCY_SYMBOL',
        ])
      })
    this.route$ = this.route.queryParams.subscribe((params) => {
      this.searchType = params.type

      switch (params.type) {
        case SearchType.PAYABLE:
          this.columns = [
            {
              prop: 'entityCode',
              name: 'ENTITY_CODE',
              template: 'entityCodePayable',
              sortable: false,
            },
            {
              prop: 'referenceId',
              name: 'REFERENCE_ID',
              sortable: false,
            },
            {
              prop: 'dataCycle',
              name: 'DATA_CYCLE',
              render: (column: any) => column.get('name'),
              sortable: false,
            },
            {
              prop: 'tax',
              name: 'TAX',
              template: 'amount',
              sortable: true,
            },
            {
              prop: 'arrears',
              name: 'ARREARS',
              template: 'arrears',
              sortable: true,
            },
            {
              prop: 'penalty',
              name: 'PENALTY',
              template: 'penalty',
              sortable: true,
            },
            {
              prop: 'discount',
              name: 'DISCOUNT',
              template: 'discount',
              sortable: true,
            },
            {
              prop: 'totalGroupDiscount',
              name: 'GROUP_DISCOUNT',
              template: 'totalGroupDiscount',
              sortable: true,
            },

            {
              prop: 'balance',
              name: 'TOTAL',
              template: 'total',
              sortable: true,
            },
          ]
          break

        case SearchType.PROPERTY_RDN:
          this.columns = [
            {
              prop: 'propertyCode',
              name: 'PROPERTY_CODE',
              template: 'propertyCode',
              sortable: false,
            },
            {
              prop: 'referenceId',
              name: 'REFERENCE_ID',
              sortable: false,
            },
            {
              prop: 'propertyType',
              name: 'Property Type',
              sortable: false,
            },
            {
              prop: 'dataCycle',
              name: 'DATA_CYCLE',
              render: (column: any) => column.get('name'),
              sortable: false,
            },
            {
              prop: 'generatedOn',
              name: 'GENERATED_ON',
              template: 'generatedOn',
              sortable: false,
            },
          ]
          break

        case SearchType.LICENSE:
          this.columns = [
            {
              prop: 'businessCode',
              name: 'CODE',
              template: 'code',
              sortable: false,
            },
            {
              prop: 'referenceId',
              name: 'REFERENCE_ID',
              sortable: false,
            },
            {
              prop: 'dataCycle',
              name: 'DATA_CYCLE',
              render: (column: any) => column.get('name'),
              sortable: false,
            },

            {
              prop: 'generatedOn',
              name: 'GENERATED_ON',
              template: 'generatedOn',
            },
          ]
          break
        case SearchType.PAYMENT:
          this.columns = [
            {
              prop: 'entityCode',
              name: 'ENTITY_CODE',
              template: 'entityCodePayment',
              sortable: false,
            },
            {
              prop: 'referenceId',
              name: 'REFERENCE_ID',
              sortable: false,
              render: (column: any, row: any) =>
                row.getIn(['payable', 'referenceId']),
            },
            {
              prop: 'dataCycle',
              name: 'DATA_CYCLE',
              render: (column: any, row: any) =>
                row.getIn(['payable', 'dataCycle', 'name']),
              sortable: false,
            },
            {
              prop: 'amount',
              name: 'AMOUNT',
              template: 'amount',
            },
            {
              prop: 'id',
              name: 'MOPTAX_ID',
            },
            {
              prop: 'transactionId',
              name: 'BANK_ID',
              template: 'transactionId',
              sortable: false,
            },
            {
              prop: 'notes',
              name: 'NOTES',
              template: 'notes',
            },
            {
              prop: 'paymentDate',
              name: 'PAYMENT_DATE',
              template: 'paymentDate',
            },
          ]
          break

        default:
          break
      }

      this.state$ = this.store
        .pipe(select(getSearchState))
        .subscribe((data) => {
          this.state = data
          this.isLoading = data.get('isLoading')
        })

      this.store.dispatch(new UpdateField('type', params.type, 'queryParams'))

      this.store.dispatch(
        new UpdateField('keyword', params.keyword, 'queryParams'),
      )

      this.store.dispatch(
        new UpdateField('page_size', params.page_size || 20, 'queryParams'),
      )

      this.store.dispatch(
        new UpdateField('page', params.page || 1, 'queryParams'),
      )

      this.store.dispatch(
        new FetchSearchResultsRequest({ page: 1, page_size: 20, ...params }),
      )
    })
  }

  ngOnDestroy() {
    this.state$.unsubscribe()
    this.route$.unsubscribe()
    this.settings$.unsubscribe()
  }

  getUrl(row: any) {
    const url = [
      '/properties/',
      row.get('id') ? row.get('id') : row.get('property_id'),
    ]

    if (this.searchType === SearchType.PROPERTY_RDN) {
      url.push('documents')
    } else if (
      this.searchType === SearchType.LICENSE ||
      this.searchType === SearchType.PAYMENT
    ) {
      url.push('payments')
    }

    return url
  }
}
